import type { LiveObject, LsonObject, Room } from "@liveblocks/client"
import { use } from "@prismicio/editor-support/React"
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import type { ExistingDocumentData } from "../documentData"
import { wrapWithLiveblocksModel } from "./wrapWithLiveblocksModel"

// Provides a suspense-safe/permanent storage place for the suspending CollaborationRoom.
export function CollaborationRoomStateProvider(props: PropsWithChildren) {
  const { children } = props

  const roomDataInitPromise = useRef<Promise<void>>()
  const [state] = useState<ContextValue>({ setNewRoom })

  useLeaveRoomOnUnmount(state)

  function setNewRoom(args: SetNewRoomArgs) {
    const { room, leave, data } = args

    state.leave?.()

    function waitForRoomData() {
      use(room.waitUntilStorageReady())

      if (!data.isFromInitialDraft) return

      // This room's storage needs to be force updated with the initial draft, in case the room already existed
      // as the initial draft should always win and override any existing room data.
      roomDataInitPromise.current ||= new Promise((resolve) => {
        const unsubscribe = room.events.storageStatus.subscribe((status) => {
          if (status !== "synchronized") return
          unsubscribe()
          resolve()
        })

        room.getStorageSnapshot()?.update({
          document: wrapWithLiveblocksModel(
            data.document,
          ) as LiveObject<LsonObject>,
        })
      })

      use(roomDataInitPromise.current)
    }

    // Use the state as a non reactive backing store; we don't want to re-render the tree.
    state.room = room
    state.leave = leave
    state.waitForRoomData = waitForRoomData
  }

  return <Context.Provider value={state}>{children}</Context.Provider>
}

interface SetNewRoomArgs {
  room: Room
  data: ExistingDocumentData
  leave: () => void
}

interface ContextValue {
  room?: Room
  waitForRoomData?: () => void
  leave?: () => void
  setNewRoom: (args: SetNewRoomArgs) => void
}

const Context = createContext<ContextValue>({ setNewRoom: () => {} })

export function useCollaborationRoomState() {
  return useContext(Context)
}

function useLeaveRoomOnUnmount(roomState: ContextValue) {
  const mounted = useRef(false)

  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false

      setTimeout(() => {
        if (mounted.current) return
        roomState.leave?.()
      }, 200)
    }
  }, [roomState])
}
